<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { get } from 'svelte/store'
  import MainEditor from './components/MainEditor.svelte'
  import FileSystemWrapper from './components/FileSystemWrapper.svelte'
  import VaultSelector from './components/VaultSelector.svelte'
  import SettingsPage from './components/settings/SettingsPage.svelte'
  import DevMenu from './components/DevMenu.svelte'
  import PricingModal from './components/subscription/PricingModal.svelte'
  import UpgradePrompt from './components/subscription/UpgradePrompt.svelte'
  import UpdateNotificationModal from './components/modals/UpdateNotificationModal.svelte'
  import Toast from './components/Toast.svelte'
  import SubscriptionTestHandler from './components/dev/SubscriptionTestHandler.svelte'
  import ErrorNotification from './components/ErrorNotification.svelte'
  import ModalManager from './components/ModalManager.svelte'
  import QuickOpen from './components/QuickOpen.svelte'
  import PDFExportDialog from './components/PDFExportDialog.svelte'
  import DemoExpiredModal from './components/modals/DemoExpiredModal.svelte'
  import { aiServiceActions } from '$lib/stores/aiService'
  import { keyboardShortcutService } from '$lib/services/KeyboardShortcutService'
  import { startupService } from '$lib/services/StartupService'
  import { promisePool } from '$lib/utils/concurrency'
  import { useCustomEvent, useEventListener } from '$lib/hooks/useEventListener'
  import { useError } from '$lib/hooks/useError'
  import { settingsStore, settingsActions, getEditorFontSize, updateFontSizeVariables, updateFontFamilyVariables } from '$lib/stores/settings'
  import { vaultStore } from '$lib/stores/vaultStore'
  import loadingLogo from './assets/1024-han-white.png'

  let showSettings = $state(false)
  let useFileExplorer = $state(false) // 파일 탐색기 모드 플래그 (기본값 false - 안정적인 MainEditor 사용)
  let vaultInitialized = $state(false)
  let checkingForSavedVault = $state(true) // Check if we're looking for a saved vault
  let vaultChanging = $state(false) // Track vault change in progress
  let quickOpenRef = $state<QuickOpen | null>(null)
  let showPDFExport = $state(false)
  let pdfExportFileName = $state('')
  let pdfExportContent = $state('')
  
  // Error handling setup
  const { tryExecute, handleError } = useError('App')
  
  function handleOpenSettings() {
    showSettings = true
  }
  
  function handleCloseSettings() {
    showSettings = false
  }
  
  async function handleVaultSelected(isInitialSelection = false) {
    // Set loading state
    vaultChanging = true
    
    try {
      // Reinitialize storage with new vault
      const { storage } = await import('$lib/storage/StorageManager')
      await storage.reinitialize()
      
      // Content index initialization is handled by vaultStore
      // to avoid duplicate initialization issues
      // vaultStore.setVault() already initializes the content index
      
      // Load document list from the new storage (but don't open any)
      const { fileStore } = await import('$lib/stores/fileStore')
      await fileStore.loadDocuments()
      
      // Only set vaultInitialized on initial selection
      // For vault changes, it should already be true
      if (isInitialSelection || !vaultInitialized) {
        vaultInitialized = true
      }

      // Only restore tabs for vault switching, not initial startup
      // Initial startup handles tab restoration separately with priority loading
      if (!isInitialSelection) {
        await restoreTabs()
      }
    } finally {
      // Clear loading state
      vaultChanging = false
    }
  }
  
  async function restoreTabs() {
    const savedTabs = localStorage.getItem('korean-grammar-open-tabs')
    const savedActiveTab = localStorage.getItem('korean-grammar-active-tab')

    // Check if tabs are already open - if so, don't restore
    const { fileStore } = await import('$lib/stores/fileStore')
    const currentState = fileStore.getState()
    if (currentState.tabs.size > 0) {
      return
    }

    if (!savedTabs) return

    try {
      const tabIds = JSON.parse(savedTabs) as string[]
      if (!tabIds?.length) return

      const documents = currentState.documents
      const docIds = documents.map(doc => doc.id)

      // Get vault path for normalization
      const { vaultStore } = await import('$lib/stores/vaultStore')
      const vault = get(vaultStore)
      const vaultPath = vault?.vaultPath

      // Convert absolute tab IDs to relative if needed
      const normalizedTabIds = tabIds.map(tabId => {
        if (vaultPath && tabId.startsWith(vaultPath)) {
          return tabId.substring(vaultPath.length).replace(/^\//, '')
        }
        return tabId
      })

      // Convert active tab ID to relative path if needed
      let normalizedActiveTab = savedActiveTab
      if (vaultPath && savedActiveTab && savedActiveTab.startsWith(vaultPath)) {
        normalizedActiveTab = savedActiveTab.substring(vaultPath.length).replace(/^\//, '')
      }

      // Validate tab IDs against document IDs
      const validIds = new Set(docIds)

      // Resolve a tab ID, trying .md alternate form if needed
      function resolveTabId(tabId: string): string | null {
        if (validIds.has(tabId)) return tabId
        const altId = tabId.endsWith('.md') ? tabId.replace('.md', '') : tabId + '.md'
        if (validIds.has(altId)) return altId
        return null
      }

      const validTabIds: string[] = []

      // Phase 1: Restore active tab first for immediate usability
      if (normalizedActiveTab) {
        const resolvedActive = resolveTabId(normalizedActiveTab)
        if (resolvedActive) {
          try {
            await fileStore.openFile(resolvedActive, { activate: true })
            validTabIds.push(resolvedActive)
            startupService.mark('startup:active-tab-ready')
          } catch {
            // Failed to restore active tab — continue
          }
        }
      }

      // Phase 2: Restore remaining tabs in parallel with concurrency limit
      // Filter using resolved active ID to prevent duplicate opens
      const resolvedActiveId = validTabIds[0] || null
      const remainingTabs = normalizedTabIds
        .filter(id => id !== normalizedActiveTab)
        .map(id => resolveTabId(id))
        .filter((id): id is string => id !== null && id !== resolvedActiveId)

      if (remainingTabs.length > 0) {
        const tasks = remainingTabs.map(tabId => async () => {
          await fileStore.openFile(tabId, { activate: false })
          return tabId
        })

        const results = await promisePool(tasks, 3)
        for (const result of results) {
          if (result.status === 'fulfilled') {
            validTabIds.push(result.value)
          }
        }
      }

      // If active tab wasn't restored, activate first valid tab
      if ((!normalizedActiveTab || !validTabIds.includes(normalizedActiveTab)) && validTabIds.length > 0) {
        await fileStore.setActiveTab(validTabIds[0])
      }

      startupService.mark('startup:all-tabs-ready')
    } catch (e) {
      console.error('[TAB-RESTORE] Failed to restore tabs:', e)
    }
  }
  
  // Handle QuickOpen events
  async function handleQuickOpenFile(event: CustomEvent<{ file: any, newTab: boolean }>) {
    const { file, newTab } = event.detail
    const { fileStore } = await import('$lib/stores/fileStore')
    
    // Open the file using its id
    await fileStore.openFile(file.id, { 
      activate: !newTab
    })
  }
  
  async function handleQuickCreateFile(event: CustomEvent<{ name: string }>) {
    const { name } = event.detail
    const { storage } = await import('$lib/storage/StorageManager')
    const { fileStore } = await import('$lib/stores/fileStore')
    
    // Create a new document with the given name
    const id = await storage.createDocument(name, '')
    if (id) {
      // Reload documents to include the new one
      await fileStore.loadDocuments()
      // Open the newly created document
      await fileStore.openFile(id, { activate: true })
      
      // Open posting prompt for new document
      const { postingPromptStore } = await import('$lib/stores/postingPrompt')
      const { unifiedPromptStore } = await import('$lib/stores/unifiedPrompt')
      postingPromptStore.loadForTab(id)
      unifiedPromptStore.show('posting')
    }
  }
  
  // Update keyboard service when settings state changes
  $effect(() => {
    keyboardShortcutService.setSettingsOpen(showSettings)
  })
  
  // Event listeners using hooks
  useEventListener(window, 'keydown', (e: KeyboardEvent) => {
    // Removed Cmd/Ctrl + Shift + F handler - now handled by menu/search component

    // Don't handle shortcuts when GenerationBar input is focused
    const activeElement = document.activeElement
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Only allow specific shortcuts
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Allow Cmd+K to close GenerationBar
        keyboardShortcutService.handleKeydown(e)
      }
      // Let browser handle all other keys naturally
      return
    }

    keyboardShortcutService.handleKeydown(e)
  }, { capture: true }) // Use capture phase to intercept early
  
  useCustomEvent('shortcut:toggle-settings', () => {
    showSettings = !showSettings
    keyboardShortcutService.setSettingsOpen(showSettings)
  })
  
  // Listen for open-settings event from footer button
  useCustomEvent('open-settings', () => {
    showSettings = true
    keyboardShortcutService.setSettingsOpen(true)
  })
  
  useCustomEvent('shortcut:escape', () => {
    if (showSettings) {
      showSettings = false
      keyboardShortcutService.setSettingsOpen(false)
    } else {
      window.dispatchEvent(new CustomEvent('shortcut:unfocus-editor'))
    }
  })
  
  // Toggle file explorer mode
  useCustomEvent('shortcut:toggle-file-explorer', () => {
    useFileExplorer = !useFileExplorer
    // If switching to file explorer mode, focus it after render
    if (useFileExplorer) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('shortcut:focus-explorer'))
      }, 200) // Give time for component to mount
    }
  })
  
  // Font size shortcuts
  useCustomEvent('shortcut:increase-font-size', () => {
    settingsActions.increaseFontSize()
  })
  
  useCustomEvent('shortcut:decrease-font-size', () => {
    settingsActions.decreaseFontSize()
  })
  
  useCustomEvent('shortcut:reset-font-size', () => {
    settingsActions.setFontSize('medium')
    settingsActions.updateEditor({ customFontSize: undefined })
  })
  
  // Close tab shortcut (Cmd+W)
  useCustomEvent('shortcut:close-tab', async () => {
    const { fileStore } = await import('./lib/stores/fileStore')
    const { get } = await import('svelte/store')
    const state = get(fileStore)
    if (state.activeTabId) {
      await fileStore.closeTab(state.activeTabId)
    }
  })
  
  // Save session state before unload
  async function handleBeforeUnload() {
    const { fileStore } = await import('$lib/stores/fileStore')
    if (fileStore?.saveSessionState) {
      fileStore.saveSessionState()
    }
  }
  
  // Function to hide initial loading screen
  function hideInitialLoading() {
    // In Electron, loading screen is already hidden by HTML
    if (window.electronAPI || window.api) {
      const initialLoading = document.getElementById('initial-loading')
      if (initialLoading) {
        initialLoading.remove()
      }
      return
    }
    
    // For web version, animate the fade out
    const initialLoading = document.getElementById('initial-loading')
    if (initialLoading) {
      // If already hidden, remove immediately
      if (initialLoading.classList.contains('fade-out')) {
        initialLoading.remove()
        return
      }
      initialLoading.classList.add('fade-out')
      setTimeout(() => {
        if (initialLoading.parentNode) {
          initialLoading.remove()
        }
      }, 300)
    }
  }
  
  // Initialize on mount
  onMount(async () => {
    startupService.begin()

    // Safety: ensure loading screen is removed within 10 seconds no matter what
    const loadingTimeout = setTimeout(() => {
      if (checkingForSavedVault) {
        console.warn('[Startup] Loading timeout — forcing UI display')
        checkingForSavedVault = false
        hideInitialLoading()
        startupService.mark('startup:shell-visible')
        startupService.setPhase('shell-ready')
      }
    }, 10000)

    // Check for demo mode
    const urlParams = new URLSearchParams(window.location.search)
    const isDemoMode = urlParams.get('demo') === 'true'
    const hideExplorer = urlParams.get('hideExplorer') === 'true'

    if (isDemoMode) {
      // Demo mode: Skip vault selection and setup demo environment

      // Clear any previous session data for clean demo environment
      localStorage.removeItem('korean-grammar-open-tabs')
      localStorage.removeItem('korean-grammar-active-tab')
      localStorage.removeItem('korean-grammar-folder-structure')
      localStorage.removeItem('korean-grammar:settings') // Clear settings for fresh demo

      // Initialize demo storage first
      const { storage } = await import('$lib/storage/StorageManager')
      await storage.initializeDemo() // Create a temporary in-memory storage

      // Set a fake vault to avoid errors
      const { vaultStore } = await import('$lib/stores/vaultStore')
      const fakeVault = {
        kind: 'demo' as any,
        name: 'Demo Mode',
        path: '/demo',
        vaultPath: '/demo'
      }
      // Use setVault with skipIndexing option to avoid file system access
      await vaultStore.setVault(fakeVault as any, { skipIndexing: true })

      // Force explorer to be open in demo mode to show file structure (unless hideExplorer is true)
      const { updateExplorerSettings } = await import('$lib/stores/uiSettingsStore')
      updateExplorerSettings({ isOpen: !hideExplorer })

      // Force light theme and responsive font size in demo mode
      const isMobile = window.innerWidth < 768 // md breakpoint
      const isTablet = window.innerWidth >= 768 && window.innerWidth < 1280 // xl breakpoint
      const fontSize = isMobile ? 'small' : isTablet ? 'medium' : 'xlarge'

      settingsActions.updateEditor({ theme: 'light', fontSize })
      // Apply light theme immediately to document
      const root = document.documentElement
      root.classList.remove('dark-theme', 'auto-theme')
      root.classList.add('light-theme')

      vaultInitialized = true
      checkingForSavedVault = false
      // MainEditor owns the current explorer panel; using FileSystemWrapper here
      // mounts a second file tree in demo mode.
      useFileExplorer = false

      // Hide initial loading screen
      hideInitialLoading()

      // Initialize demo files once
      const initializeDemoFiles = async () => {
        const { fileStore } = await import('$lib/stores/fileStore')
        const state = get(fileStore)

        // Check if demo files already exist
        const hasFiles = state.documents.length > 0 || state.tabs.size > 0
        if (hasFiles) {
          return
        }

        try {
          // Create documents directly using storage without opening tabs
          const { storage } = await import('$lib/storage/StorageManager')

          // Load all markdown files from templates using import.meta.glob
          const allFiles = import.meta.glob('/src/lib/templates/**/*.md', {
            query: '?raw',
            import: 'default'
          })
          const allPaths = Object.keys(allFiles)

          // Separate files into root level and folders
          const rootLevelFiles: string[] = []
          const folderMap = new Map<string, string[]>()

          for (const path of allPaths) {
            // Path format: /src/lib/templates/file.md (root) or /src/lib/templates/FOLDER_NAME/file.md
            const parts = path.split('/')

            // Check if it's a root level file (exactly 5 parts: '', 'src', 'lib', 'templates', 'file.md')
            if (parts.length === 5 && parts[3] === 'templates') {
              rootLevelFiles.push(path)
            }
            // Folder file (6+ parts: '', 'src', 'lib', 'templates', 'FOLDER_NAME', 'file.md')
            else if (parts.length >= 6) {
              const folderName = parts[4] // Extract folder name (index 4 = templates/FOLDER_NAME)
              if (!folderMap.has(folderName)) {
                folderMap.set(folderName, [])
              }
              folderMap.get(folderName)!.push(path)
            }
          }

          // First, create root level files
          let docId1: string | undefined
          for (const rootPath of rootLevelFiles) {
            const fileName = rootPath.split('/').pop() || ''
            const loadFile = allFiles[rootPath] as () => Promise<string>
            const content = await loadFile()
            const title = fileName.replace(/\.md$/, '')

            const docId = await storage.createDocument(title, content, undefined)

            // Open the first root level file by default
            if (!docId1) {
              docId1 = docId
            }
          }

          // Then, process each folder - load markdown files using import
          for (const [folderName, filePaths] of folderMap.entries()) {
            for (const filePath of filePaths) {
              const fileName = filePath.split('/').pop() || ''
              // Load file content using the glob import
              const loadFile = allFiles[filePath] as () => Promise<string>
              const content = await loadFile()
              const title = fileName.replace(/\.md$/, '')
              await storage.createDocument(title, content, folderName)
            }
          }

          // Note: Images in templates folders are automatically detected by FileExplorer
          // No need to create documents for them - they will show as actual image files

          // Load documents list to update the file store
          await fileStore.loadDocuments()

          // Note: demo-images folder images are automatically detected by FileExplorer
          // No need to create documents for them - they will show as actual image files

          // Only open the first document (AI시대의 글쓰기) initially if it was created
          if (docId1) {
            await fileStore.openFile(docId1)
          }

          // Trigger file explorer refresh
          window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
        } catch (error) {
          console.error('Failed to initialize demo files:', error)
        }
      }

      // Initialize demo files after a short delay
      setTimeout(initializeDemoFiles, 500)

      // Listen for messages from parent window (navigation and tab opening)
      window.addEventListener('message', async (event) => {
        const { fileStore } = await import('$lib/stores/fileStore')
        const state = get(fileStore)

        if (event.data?.type === 'show-pricing') {
          // Find the pricing document and open it if not already opened
          const pricingDoc = state.documents.find((doc: any) => doc.title === '요금제 안내' || doc.title === '요금제')
          if (pricingDoc) {
            // Check if already open
            const isOpen = state.tabs.has(pricingDoc.id)
            if (!isOpen) {
              await fileStore.openFile(pricingDoc.id)
            } else {
              await fileStore.setActiveTab(pricingDoc.id)
            }
          }
        } else if (event.data?.type === 'show-guide') {
          // Find the first guide document (시작하기)
          const firstGuideDoc = state.documents.find((doc: any) =>
            doc.title === '시작하기'
          )

          if (firstGuideDoc) {
            // Check if already open
            const isOpen = state.tabs.has(firstGuideDoc.id)
            if (!isOpen) {
              // Open only the first guide document
              await fileStore.openFile(firstGuideDoc.id)
            } else {
              // Just activate if already open
              await fileStore.setActiveTab(firstGuideDoc.id)
            }
          }
        }
      })


      clearTimeout(loadingTimeout)
      return // Skip normal initialization in demo mode
    }

    // Normal initialization
    // Add beforeunload listener
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Listen for vault changing events from VaultDropdown
    window.addEventListener('vault-changing-start', () => {
      vaultChanging = true
    })

    // Listen for vault selected event to handle vault change completion
    window.addEventListener('vault-selected', async () => {
      await handleVaultSelected()
    })

    // Check for vault path and selector flag in URL query parameters
    const vaultPathFromUrl = urlParams.get('vaultPath')
    const selectVaultFlag = urlParams.get('selectVault') === 'true'

    // Determine which vault to open
    let vaultPathToOpen: string | null = null
    let vaultNameToUse: string | null = null

    if (selectVaultFlag) {
      // Explicitly requested to show vault selector - don't auto-load
      vaultPathToOpen = null
      vaultNameToUse = null
    } else if (vaultPathFromUrl) {
      // New window with specific vault
      vaultPathToOpen = decodeURIComponent(vaultPathFromUrl)
      vaultNameToUse = vaultPathToOpen.split('/').pop() || vaultPathToOpen.split('\\').pop() || 'vault'
    } else {
      // App startup - check for saved vault preference
      const savedVaultPath = localStorage.getItem('korean-grammar-vault-path')
      const savedVaultName = localStorage.getItem('korean-grammar-current-vault')
      vaultPathToOpen = savedVaultPath
      vaultNameToUse = savedVaultName
    }

    // Try to auto-open vault in Electron
    if (vaultPathToOpen && window.api) {
      try {
        startupService.setPhase('vault-resolving')
        const result = await window.api.openVault(vaultPathToOpen)
        startupService.mark('startup:vault-path-resolved')

        if (result.success && result.path) {
          // Create vault handle
          const fakeHandle = {
            kind: 'directory',
            name: vaultNameToUse || result.path.split('/').pop() || result.path.split('\\').pop() || 'vault',
            path: result.path
          } as any

          // setVault completes (content index is fire-and-forget inside vaultStore)
          await vaultStore.setVault(fakeHandle)

          // Show UI shell immediately — don't wait for documents/tabs/indexing
          checkingForSavedVault = false
          hideInitialLoading()
          clearTimeout(loadingTimeout)
          startupService.mark('startup:shell-visible')
          startupService.setPhase('shell-ready')

          // Background: load documents
          startupService.setPhase('documents-loading')
          await handleVaultSelected(true)
          startupService.mark('startup:documents-loaded')

          // Restore tabs with priority (active tab first)
          startupService.setPhase('restoring-tabs')
          await restoreTabs()

          // Background tasks phase
          startupService.setPhase('background-tasks')
        } else {
          checkingForSavedVault = false
          hideInitialLoading()
          clearTimeout(loadingTimeout)
          startupService.mark('startup:shell-visible')
          startupService.setPhase('shell-ready')
        }
      } catch (error) {
        console.error('Failed to auto-open vault:', error)
        // Fall back to manual selection - show vault selector
        checkingForSavedVault = false
        hideInitialLoading()
        clearTimeout(loadingTimeout)
        startupService.mark('startup:shell-visible')
        startupService.setPhase('shell-ready')
      }
    } else {
      // No vault to open, show selector
      checkingForSavedVault = false
      hideInitialLoading()
      clearTimeout(loadingTimeout)
      startupService.mark('startup:shell-visible')
      startupService.setPhase('shell-ready')
    }
    
    // Listen for IPC font size messages from Electron menu
    if (window.electronAPI && window.electronAPI.on) {
      window.electronAPI.on('font-size', (action: string) => {
        if (action === 'increase') {
          settingsActions.increaseFontSize()
        } else if (action === 'decrease') {
          settingsActions.decreaseFontSize()
        } else if (action === 'reset') {
          settingsActions.setFontSize('medium')
          settingsActions.updateEditor({ customFontSize: undefined })
        }
      })
      
      // Listen for Quick Open from menu
      window.electronAPI.on('file:open-quickly', () => {
        quickOpenRef?.open()
      })
      
      // Listen for Close Tab from menu
      window.electronAPI.on('file:close-tab', async () => {
        const { fileStore } = await import('./lib/stores/fileStore')
        const { get } = await import('svelte/store')
        const state = get(fileStore)
        if (state.activeTabId) {
          await fileStore.closeTab(state.activeTabId)
        }
      })
      
      // Listen for Export to PDF from menu
      window.electronAPI.on('menu:export-pdf', async () => {
        const { fileStore } = await import('./lib/stores/fileStore')
        const { get } = await import('svelte/store')
        const state = get(fileStore)
        
        // tabs is a Map, not an array
        const activeTab = state.activeTabId ? state.tabs.get(state.activeTabId) : null
        
        if (activeTab) {
          // Get the filename without extension
          const fileName = activeTab.title || 'Untitled'
          pdfExportFileName = fileName.replace(/\.[^/.]+$/, '') // Remove extension
          pdfExportContent = activeTab.content || ''
          showPDFExport = true
        } else {
          // If no active tab, show notification or create empty document
          pdfExportFileName = 'Untitled'
          pdfExportContent = ''
          showPDFExport = true
        }
      })
    }
    
    // Initialize theme, font size, and font family from settings
    import('./lib/stores/settings').then(({ settingsStore, getEditorFontSize, updateFontSizeVariables, updateFontFamilyVariables }) => {
      const settings = settingsStore.get()

      // Initialize theme
      if (settings.editor?.theme) {
        const root = document.documentElement
        if (settings.editor.theme === 'light') {
          root.classList.add('light-theme')
        } else if (settings.editor.theme === 'dark') {
          root.classList.add('dark-theme')
        }
        // 'auto' doesn't need any class
      }

      // Initialize font size CSS variables
      const fontSize = getEditorFontSize(settings.editor)
      updateFontSizeVariables(fontSize)

      // Initialize font family CSS variables
      if (settings.editor?.fontFamily) {
        updateFontFamilyVariables(settings.editor.fontFamily)
      }
    })
    
    // Initialize AI service on app mount — fire-and-forget, don't block startup
    startupService.mark('startup:ai-init-start')
    tryExecute(
      () => aiServiceActions.initialize(),
      {
        severity: 'warning',
        context: { action: 'initializeAIService' },
        showToUser: false,
        logToConsole: true
      }
    ).then(([_result, error]) => {
      startupService.mark('startup:ai-ready')
      if (error) {
        // Silently fail but log - AI service will work when API keys are added
        handleError(error, {
          severity: 'info',
          context: { action: 'aiServiceInitialization' },
          showToUser: false,
          logToConsole: true
        })
      }
    })
    
    // Listen for IPC settings event
    if (window.electronAPI && window.electronAPI.onNavigate) {
      window.electronAPI.onNavigate((route: string) => {
        if (route === 'settings') {
          showSettings = true
        }
      })
    }
    
    // Listen for vault menu events
    if (window.api && window.api.onMenuAction) {
      // Open vault
      window.api.onMenuAction('vault:open', async () => {
        // Don't change vaultInitialized, just trigger vault change
        vaultChanging = true
        vaultStore.clearVault()
        const { fileStore } = await import('$lib/stores/fileStore')
        await fileStore.closeAllTabs()
        vaultInitialized = false // Only set false after clearing to show selector
        vaultChanging = false
      })
      
      // Close vault
      window.api.onMenuAction('vault:close', async () => {
        vaultInitialized = false
        vaultStore.clearVault()
        const { fileStore } = await import('$lib/stores/fileStore')
        await fileStore.closeAllTabs()
      })
      
      // Change vault
      window.api.onMenuAction('vault:change', async () => {
        // Don't change vaultInitialized during change
        vaultChanging = true
        vaultStore.clearVault()
        const { fileStore } = await import('$lib/stores/fileStore')
        await fileStore.closeAllTabs()
        vaultInitialized = false // Only set false after clearing to show selector
        vaultChanging = false
      })
      
      // Create new vault
      window.api.onMenuAction('vault:create', async () => {
        vaultChanging = true
        vaultStore.clearVault()
        const { fileStore } = await import('$lib/stores/fileStore')
        await fileStore.closeAllTabs()
        vaultInitialized = false // Only set false after clearing to show selector
        vaultChanging = false
      })
    }
    
    // Listen for vault:clear-recent from menu
    if (window.electronAPI && window.electronAPI.on) {
      window.electronAPI.on('vault:clear-recent', () => {
        localStorage.removeItem('korean-grammar-recent-vaults')
        // Update menu to show empty state
        if (window.electronAPI) {
          window.electronAPI.send('update-recent-vaults', [])
        }
      })
      
      // Listen for vault:open-recent from menu
      window.electronAPI.on('vault:open-recent', async (vaultPath: string) => {
        try {
          if (window.api) {
            // Start vault change process
            vaultChanging = true
            vaultStore.clearVault()
            const { fileStore } = await import('$lib/stores/fileStore')
            await fileStore.closeAllTabs()
            
            // Open the recent vault
            const result = await window.api.openVault(vaultPath)
            if (result.success && result.path) {
              const vaultName = result.path.split('/').pop() || result.path.split('\\').pop() || 'vault'
              const fakeHandle = {
                kind: 'directory',
                name: vaultName,
                path: result.path
              } as any
              
              await vaultStore.setVault(fakeHandle)
              await handleVaultSelected() // This is a vault change, not initial selection
              
              // Update recent vaults list
              const saved = localStorage.getItem('korean-grammar-recent-vaults')
              if (saved) {
                try {
                  const recentVaults = JSON.parse(saved)
                  const updated = [
                    { name: vaultName, path: result.path },
                    ...recentVaults.filter((v: any) => v.path !== result.path)
                  ].slice(0, 5)
                  localStorage.setItem('korean-grammar-recent-vaults', JSON.stringify(updated))
                  
                  // Update menu
                  if (window.electronAPI) {
                    const vaultPaths = updated.map((v: any) => v.path)
                    window.electronAPI.send('update-recent-vaults', vaultPaths)
                  }
                } catch (e) {
                  console.error('Failed to update recent vaults:', e)
                }
              }
            }
          }
        } catch (error) {
          console.error('Failed to open recent vault:', error)
          vaultChanging = false // Clear loading state on error
        }
      })
    }
    
    // Send initial recent vaults to menu on startup
    const savedVaults = localStorage.getItem('korean-grammar-recent-vaults')
    if (savedVaults && window.electronAPI) {
      try {
        const recentVaults = JSON.parse(savedVaults)
        const vaultPaths = recentVaults.map((v: any) => v.path || v)
        window.electronAPI.send('update-recent-vaults', vaultPaths)
      } catch (e) {
        console.error('Failed to send recent vaults:', e)
      }
    }

    startupService.setPhase('ready')
  })
  
  // Cleanup on destroy
  onDestroy(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    
    // Save session state
    import('$lib/stores/fileStore').then(({ fileStore }) => {
      if (fileStore?.saveSessionState) {
        fileStore.saveSessionState()
      }
    })
  })
</script>

<!-- Global Error Notification -->
<ErrorNotification />

<main class="app-container" aria-label="Korean Grammar Checker 앱">
  {#if vaultChanging}
    <!-- Show loading screen during vault change -->
    <div class="initial-loading-style">
      <img class="loading-logo" src={loadingLogo} alt="Korean Grammar Checker" />
      <div class="loading-spinner"></div>
      <p class="loading-text">SWITCHING VAULT</p>
    </div>
  {:else if !checkingForSavedVault && !vaultInitialized}
    <!-- Show vault selector only if no saved vault and done checking -->
    <VaultSelector onVaultSelected={() => handleVaultSelected(true)} />
  {:else if vaultInitialized}
    <!-- Show editor after vault is selected -->
    <div style="display: {showSettings ? 'none' : 'block'}; height: 100%;">
      {#if useFileExplorer}
        <FileSystemWrapper onOpenSettings={handleOpenSettings} />
      {:else}
        <MainEditor onOpenSettings={handleOpenSettings} />
      {/if}
    </div>
    
    {#if showSettings}
      <SettingsPage onClose={handleCloseSettings} />
    {/if}
  {/if}
  
  <!-- Dev Menu (always rendered but hidden by default) -->
  <DevMenu />
  
  <!-- Subscription Test Handler (dev only) -->
  <SubscriptionTestHandler />
  
  <!-- Subscription Modals -->
  <PricingModal />
  <UpgradePrompt />

  <!-- Update Notification Modal -->
  <UpdateNotificationModal />

  <!-- Toast Notifications -->
  <Toast />

  <!-- Demo Expired Modal -->
  <DemoExpiredModal />

  <!-- Modal Manager -->
  <ModalManager />
  
  <!-- Quick Open -->
  {#if vaultInitialized}
    <QuickOpen 
      bind:this={quickOpenRef}
      on:openFile={handleQuickOpenFile}
      on:createFile={handleQuickCreateFile}
    />
  {/if}
  
  <!-- PDF Export Dialog -->
  <PDFExportDialog
    bind:isOpen={showPDFExport}
    fileName={pdfExportFileName}
    content={pdfExportContent}
    on:success={(event) => {
      showPDFExport = false
    }}
    on:error={(event) => {
      console.error('PDF export failed:', event.detail)
      showPDFExport = false
    }}
    on:close={() => {
      showPDFExport = false
    }}
  />
</main>

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }

  .app-container {
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    background: var(--bg-secondary);
  }
  
  /* Reuse initial loading screen style for vault change */
  .initial-loading-style {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #0a0a0a;
    z-index: 99999;
  }
  
  .loading-logo {
    width: 64px;
    height: 64px;
    margin-bottom: 24px;
    opacity: 0.95;
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.95; }
    50% { opacity: 0.6; }
  }
  
  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 2px solid rgba(124, 122, 255, 0.2);
    border-top-color: #7c7aff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  .loading-text {
    margin-top: 16px;
    font-size: 12px;
    color: #666666;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* CSS Variables for layout */
  :global(:root) {
    --layout-input-width: 60%;
    --layout-results-width: 40%;
  }
</style>
